import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks';
import { TrialBanner } from '../components/dashboard';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner, Alert } from '../components/ui';
import { OverviewCards, TopPerformersTable } from '../components/analytics';
import { Search, Plus } from 'lucide-react';
import { api } from '../services/api';

interface Stats {
  totalClients: number;
  totalServers: number;
  totalQueries: number;
  totalExecutions: number;
}

export const Dashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    refreshUser();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const userData = await api.users.me();
      setStats({
        totalClients: userData.totalClients || 0,
        totalServers: userData.totalServers || 0,
        totalQueries: userData.totalQueries || 0,
        totalExecutions: userData.totalExecutions || 0
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Welcome back, {user?.email}
        </p>
      </div>

      {/* Trial Banner */}
      {user && (
        <TrialBanner
          trialEndsAt={user.trialEndsAt}
          trialExpired={user.trialExpired}
          plan={user.plan}
        />
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Analytics Overview */}
      <OverviewCards />

      {/* Top Performers */}
      <TopPerformersTable limit={10} />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/clients" className="block">
              <Button variant="secondary" fullWidth>
                <Plus size={18} className="mr-2" />
                Create Client
              </Button>
            </Link>
            <Link to="/servers" className="block">
              <Button variant="secondary" fullWidth>
                <Plus size={18} className="mr-2" />
                Connect Server
              </Button>
            </Link>
            <Link to="/query" className="block">
              <Button variant="secondary" fullWidth>
                <Search size={18} className="mr-2" />
                Search Tools
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      {stats && stats.totalClients === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Getting Started</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600">
                Welcome to MCP Hub Universal! Here's how to get started:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Create your first client to group your MCP servers</li>
                <li>Connect MCP servers (HTTP, SSE, WebSocket, etc.)</li>
                <li>Use semantic search to find relevant tools</li>
                <li>Execute tools directly from the platform</li>
              </ol>
              <div className="pt-4">
                <Link to="/clients">
                  <Button variant="primary">
                    Create Your First Client
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Info */}
      {user && (
        <Card>
          <CardHeader>
            <CardTitle>Your Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-gray-900 uppercase">
                  {user.plan} Plan
                </p>
                {user.plan === 'free' && user.trialEndsAt && !user.trialExpired && (
                  <p className="text-sm text-gray-600 mt-1">
                    Trial ends on {new Date(user.trialEndsAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              {user.plan === 'free' && (
                <Link to="/settings">
                  <Button variant="primary">
                    Upgrade Plan
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
